/**
 * Hooks for Challenge queries (Route and Individual Challenges)
 * Replaces static API methods with react-query hooks
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useBackend } from './useBackend';
import { createRouteChallenge } from '../Model/Engagement/RouteChallenge/RouteChallenge';
import { createIndividualChallenge } from '../Model/Engagement/IndividualChallenge/IndividualChallenge';
import { createRouteChallengeTeam } from '../Model/Engagement/RouteChallenge/RouteChallengeTeam';
import { createIndividualChallengeParticipant } from '../Model/Engagement/IndividualChallenge/IndividualChallengeParticipant';
import { createRouteChallengeTeamMember, type RouteChallengeTeamMember } from '../Model/Engagement/RouteChallenge/RouteChallengeTeamMember';

// Route Challenge Hooks
export function useRouteChallenge(id?: string) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['route-challenge', id],
    queryFn: async () => {
      if (!id) return undefined;
      const json = await backend.get(`/coach-program/engagement/route-challenges/${id}`);
      return json ? createRouteChallenge(json) : undefined;
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCurrentRouteChallenges() {
  const backend = useBackend();

  return useQuery({
    queryKey: ['route-challenges', 'current'],
    queryFn: async () => {
      const json = await backend.get('/coach-program/engagement/route-challenges/current');
      return (json ?? []).map((c: any) => createRouteChallenge(c));
    },
    staleTime: 2 * 60 * 1000,
  });
}

// Individual Challenge Hooks
export function useIndividualChallenge(id?: string) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['individual-challenge', id],
    queryFn: async () => {
      if (!id) return undefined;
      const json = await backend.get(`/coach-program/engagement/individual-challenges/${id}`);
      return json ? createIndividualChallenge(json) : undefined;
    },
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
}

export function useCurrentIndividualChallenges() {
  const backend = useBackend();

  return useQuery({
    queryKey: ['individual-challenges', 'current'],
    queryFn: async () => {
      const json = await backend.get('/coach-program/engagement/individual-challenges/current');
      return (json ?? []).map((c: any) => createIndividualChallenge(c));
    },
    staleTime: 2 * 60 * 1000,
  });
}

// Challenge Participation Hooks
export function useIndividualChallengeParticipation(challengeId?: string) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['individual-challenge-participation', challengeId],
    queryFn: async () => {
      if (!challengeId) return undefined;
      const json = await backend.get(`/coach-program/engagement/individual-challenges/${challengeId}/my-participation`);
      return json ? createIndividualChallengeParticipant(json) : undefined;
    },
    enabled: !!challengeId,
    staleTime: 30 * 1000,
    retry: false,
  });
}

export function useChallengeRanking(challengeId?: string, options?: { page?: number; size?: number }) {
  const backend = useBackend();
  const { page = 0, size = 20 } = options ?? {};

  return useQuery({
    queryKey: ['individual-challenge-ranking', challengeId, page, size],
    queryFn: async () => {
      if (!challengeId) return [];
      const json = await backend.get(`/coach-program/engagement/individual-challenges/${challengeId}/ranking/participants`, {
        page,
        size,
      });
      return (json ?? []).map((p: any) => createIndividualChallengeParticipant(p));
    },
    enabled: !!challengeId,
    staleTime: 60 * 1000,
  });
}

// Team Hooks
export function useRouteChallengeTeamMembers(teamId?: string) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['team-members', teamId],
    queryFn: async () => {
      if (!teamId) {
        return [];
      }
      const json = await backend.get<Partial<RouteChallengeTeamMember>[]>(
        `/coach-program/engagement/route-challenges/teams/${teamId}/members`,
      );
      return (json ?? []).map(createRouteChallengeTeamMember);
    },
    enabled: !!teamId,
    staleTime: 30 * 1000,
    retry: false,
  });
}

export function useMyRouteChallengeTeam(challengeId?: string) {
  const backend = useBackend();

  return useQuery({
    queryKey: ['my-team', challengeId],
    queryFn: async () => {
      if (!challengeId) return undefined;
      const json = await backend.get('/coach-program/engagement/route-challenges/teams/my-team', { challengeId });
      return json ? createRouteChallengeTeam(json) : undefined;
    },
    enabled: !!challengeId,
    staleTime: 30 * 1000,
    retry: false,
  });
}

export function useTeamRanking(challengeId?: string, options?: { page?: number; size?: number }) {
  const backend = useBackend();
  const { page = 0, size = 20 } = options ?? {};

  return useQuery({
    queryKey: ['team-ranking', challengeId, page, size],
    queryFn: async () => {
      if (!challengeId) return [];
      const json = await backend.get('/coach-program/engagement/route-challenges/teams', {
        challengeId,
        page,
        size,
        sort: 'companyRank,ASC',
      });
      return (json ?? []).map((t: any) => createRouteChallengeTeam(t));
    },
    enabled: !!challengeId,
    staleTime: 60 * 1000,
  });
}

// Challenge Mutations
export function useJoinIndividualChallenge() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (challengeId: string) =>
      backend.post(`/coach-program/engagement/individual-challenges/${challengeId}/join`),
    onSuccess: (_data, challengeId) => {
      queryClient.invalidateQueries({ queryKey: ['individual-challenge-participation', challengeId] });
      queryClient.invalidateQueries({ queryKey: ['individual-challenge-ranking', challengeId] });
    },
  });
}

export function useLeaveIndividualChallenge() {
  const backend = useBackend();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (challengeId: string) =>
      backend.post(`/coach-program/engagement/individual-challenges/${challengeId}/leave`),
    onSuccess: (_data, challengeId) => {
      queryClient.invalidateQueries({ queryKey: ['individual-challenge-participation', challengeId] });
      queryClient.invalidateQueries({ queryKey: ['individual-challenge-ranking', challengeId] });
    },
  });
}
